import { useCallback, useMemo, useState } from "react";
import { ProfileCard } from "../../../types/profile";
import {
  BureauDashboardData,
  BureauDashboardSection,
  ProfileSectionConfig,
} from "../dashboard.types";
import {
  normalizeProfileCard,
  parseNumericValue,
} from "../dashboard.utils";

type UseProfileSectionsOptions = {
  defaultSections: ProfileSectionConfig[];
};

const useProfileSections = ({
  defaultSections,
}: UseProfileSectionsOptions) => {
  const [profileSections, setProfileSections] =
    useState<ProfileSectionConfig[]>(defaultSections);

  const defaultSectionMap = useMemo(
    () =>
      new Map(
        defaultSections.map(
          (section) => [section.key, section] as const,
        ),
      ),
    [defaultSections],
  );

  const buildSectionSourceFromPayload = useCallback(
    (
      data: BureauDashboardData | null,
    ): Record<string, BureauDashboardSection> => {
      if (!data) {
        return {};
      }

      const sameBureau = data.sameBureau ?? {};
      const sameBureauPending = data.sameBureauPending ?? {};
      const otherBureau = data.otherBureau ?? {};

      return {
        "private-profiles": {
          key: "private-profiles",
          profiles: data.privateProfiles?.data ?? [],
          totalCount: data.privateProfiles?.total,
        },
        "my-male": {
          key: "my-male",
          profiles: sameBureau?.male?.data ?? [],
          totalCount: sameBureau?.male?.total,
        },

        "my-female": {
          key: "my-female",
          profiles: sameBureau?.female?.data ?? [],
          totalCount: sameBureau?.female?.total,
        },

        "pending-male": {
          key: "pending-male",
          profiles: sameBureauPending?.male?.data ?? [],
          totalCount: sameBureauPending?.male?.total,
        },

        "pending-female": {
          key: "pending-female",
          profiles: sameBureauPending?.female?.data ?? [],
          totalCount: sameBureauPending?.female?.total,
        },

        "other-male": {
          key: "other-male",
          profiles: otherBureau?.male?.data ?? [],
          totalCount: otherBureau?.male?.total,
        },

        "other-female": {
          key: "other-female",
          profiles: otherBureau?.female?.data ?? [],
          totalCount: otherBureau?.female?.total,
        },

        "free-profiles": {
          key: "free-profiles",
          profiles: data.freeProfiles?.data ?? [],
          totalCount: data.freeProfiles?.total,
        },

        "paid-profiles": {
          key: "paid-profiles",
          profiles: data.paidProfiles?.data ?? [],
          totalCount: data.paidProfiles?.total,
        },

        "redzone-profiles": {
          key: "redzone-profiles",
          profiles: data.redzoneProfiles?.data ?? [],
          totalCount: data.redzoneProfiles?.total,
        },

        "other-mediator": {
          key: "other-mediator",
          profiles: data.otherMediatorProfiles?.data ?? [],
          totalCount: data.otherMediatorProfiles?.total,
        },

        shortlisted: {
          key: "shortlisted",
          profiles: data.shortlistedProfiles?.data ?? [],
          totalCount: data.shortlistedProfiles?.total,
        },

        "callback-profiles": {
          key: "callback-profiles",
          profiles:
            data.callBackProfiles?.data ??
            data.callBackProfiles ??
            [],
          totalCount:
            data.callBackProfiles?.total ??
            data.callBackProfiles?.length ??
            0,
        },

        "not-responding-profiles": {
          key: "not-responding-profiles",
          profiles:
            data.notRespondingProfiles?.data ??
            data.notRespondingProfiles ??
            [],
          totalCount:
            data.notRespondingProfiles?.total ??
            data.notRespondingProfiles?.length ??
            0,
        },
      };
    },
    [],
  );

  const normalizeSection = useCallback(
    (
      sectionKey: string,
      rawSection: BureauDashboardSection,
      index: number,
    ): ProfileSectionConfig | null => {
      const fallbackSection =
        defaultSectionMap.get(sectionKey) ??
        (rawSection?.key
          ? defaultSectionMap.get(rawSection.key)
          : undefined) ??
        defaultSections[
          Math.min(index, defaultSections.length - 1)
        ];

      if (!fallbackSection) {
        return null;
      }

      const rawProfiles = Array.isArray(rawSection?.profiles)
        ? rawSection.profiles
        : Array.isArray(rawSection)
          ? (rawSection as unknown as any[])
          : [];

      const normalizedProfiles: ProfileCard[] =
        rawProfiles.map((profile, profileIndex) =>
          normalizeProfileCard(
            profile,
            profileIndex,
            rawSection?.key ??
              sectionKey ??
              fallbackSection.key,
          ),
        );

      return {
        ...fallbackSection,
        key:
          rawSection?.key ??
          sectionKey ??
          fallbackSection.key,
        title:
          rawSection?.title ??
          fallbackSection.title,
        profiles: normalizedProfiles,
        totalCount:
          parseNumericValue(rawSection?.totalCount) ??
          parseNumericValue(rawSection?.count) ??
          fallbackSection.totalCount ??
          normalizedProfiles.length,
        countLabel:
          rawSection?.countLabel ??
          fallbackSection.countLabel,
        viewAllText:
          rawSection?.viewAllText ??
          fallbackSection.viewAllText,
        viewAllPath:
          rawSection?.viewAllPath ??
          fallbackSection.viewAllPath,
        statusBadgeClass:
          rawSection?.statusBadgeClass ??
          fallbackSection.statusBadgeClass,
        headerGradientClass:
          rawSection?.headerGradientClass ??
          fallbackSection.headerGradientClass,
        navButtonClass:
          rawSection?.navButtonClass ??
          fallbackSection.navButtonClass,
        buttonClass:
          rawSection?.buttonClass ??
          fallbackSection.buttonClass,
        countColorClass:
          rawSection?.countColorClass ??
          fallbackSection.countColorClass,
      };
    },
    [defaultSectionMap, defaultSections],
  );

  const normalizeSections = useCallback(
    (rawSections: unknown): ProfileSectionConfig[] => {
      if (!rawSections) {
        return defaultSections;
      }

      const entries: Array<{
        key: string;
        value: BureauDashboardSection;
      }> = [];

      if (Array.isArray(rawSections)) {
        rawSections.forEach((section: any, index) => {
          const key =
            section?.key ??
            section?.id ??
            section?.slug ??
            section?.type ??
            `section-${index}`;

          entries.push({
            key,
            value: section,
          });
        });
      } else if (typeof rawSections === "object") {
        Object.entries(
          rawSections as Record<string, unknown>,
        ).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            entries.push({
              key,
              value: {
                key,
                profiles: value,
              },
            });
            return;
          }

          entries.push({
            key,
            value: {
              key,
              ...(value as BureauDashboardSection),
            },
          });
        });
      }

      if (entries.length === 0) {
        return defaultSections;
      }

      const normalizedByKey = new Map<
        string,
        ProfileSectionConfig
      >();

      entries.forEach(({ key, value }, index) => {
        const normalized = normalizeSection(
          key,
          value,
          index,
        );

        if (normalized) {
          normalizedByKey.set(
            normalized.key,
            normalized,
          );
        }
      });

      const mergedSections = defaultSections.map(
        (section) => {
          const override = normalizedByKey.get(
            section.key,
          );

          if (!override) {
            return section;
          }

          normalizedByKey.delete(section.key);
          return override;
        },
      );

      normalizedByKey.forEach((section) => {
        mergedSections.push(section);
      });

      return mergedSections;
    },
    [defaultSections, normalizeSection],
  );

  const applyProfileSections = useCallback(
    (payload: BureauDashboardData) => {
      const sectionSource =
        buildSectionSourceFromPayload(payload);

      const normalized =
        normalizeSections(sectionSource);

      setProfileSections(
        normalized.length > 0
          ? normalized
          : defaultSections,
      );
    },
    [
      buildSectionSourceFromPayload,
      defaultSections,
      normalizeSections,
    ],
  );

  return {
    profileSections,
    setProfileSections,
    applyProfileSections,
    normalizeSections,
  };
};

export default useProfileSections;